import { useContext, useState } from 'react';
import { getCardLogo } from '@unified-checkout/kit-components';
import {
  Badge,
  Button,
  FeaturedIcon,
  Modal,
  Notification,
} from '@unified-checkout/design-system';
import { TrashCan } from '@unified-checkout/design-system/icons/index';
import {
  useDeleteCard,
  useSetDefaultCard,
} from 'apps/customer/src/services/inner_layout/paymentMethods';
import { DeviceContext } from 'apps/customer/src/context/device';
import { IPaymentMethod } from 'apps/customer/src/types';
import { endOfMonth, isAfter, parse } from 'date-fns';

interface IPaymentCardItemProps {
  method: IPaymentMethod;
}
export const PaymentCardItem = ({ method }: IPaymentCardItemProps) => {
  const { isMobile } = useContext(DeviceContext);
  const [isDeleteCardModalOpen, setIsDeleteCardModalOpen] = useState(false);
  const {
    card_subtype,
    expiry_month,
    expiry_year,
    is_default,
    unique_id,
    issuing_bank,
    masked_pan,
    vts_content
  } = method;
  const icon = getCardLogo(card_subtype);
  const  content = vts_content?.content[0] || {}
  const {encodedData, mimeType, width, height} = content
  const { mutate: setIsDefaultMutation, isPending: isPendingSetDefault } =
    useSetDefaultCard();

  const {
    mutate: deleteCardMutation,
    isPending: isPendingDeleteCard,
    isSuccess: isSuccessDeleting,
    reset: resetDeleteMutation,
  } = useDeleteCard();

  const deleteCardHandler = () => {
    deleteCardMutation({ unique_id });
    deleteCardModalCloseHandler();
  };

  const deleteCardModalCloseHandler = () => setIsDeleteCardModalOpen(false);

  const hasMonthYearExpired = (month: number, year: number) =>{
    const expirationDate = parse(`20${year}-${month}-01`, 'yyyy-MM-dd', new Date());
    const endOfExpirationMonth = endOfMonth(expirationDate);

    return isAfter(new Date(), endOfExpirationMonth);
}

  const expiration = String(expiry_month).padStart(2, '0') + '/' + expiry_year;
  const isExpired = hasMonthYearExpired(expiry_month, expiry_year);
  
  return (
    <section className="flex gap-3 p-4 border rounded-xl">
      {(icon || encodedData) && (
        <div className="w-[2.875rem] h-8 mt-2 rounded-md overflow-hidden">
          {encodedData ? (
            <img
              src={`data:${mimeType};base64,${encodedData}`}
              alt="card-logo"
              className='h-full'
            />
          ) : (
            icon
          )}
        </div>
      )}
      <section>
        <div className="mb-3">
          <p className="mb-1 text-gray-900 english-utility-label-semibold">
            {issuing_bank} {masked_pan}
          </p>
          {isExpired ? <p className='text-red-600 font-sm leading-[14px] '>Expired</p> : <p className="text-gray-600 english-utility-label-compact-regular">
            Expires {expiration}
          </p>}
        </div>
        <section className="flex gap-2">
          {!isExpired &&
            (is_default ? (
              <div className="[&_*]:rounded-lg">
                <Badge text="Default" color="gray" size="lg" />
              </div>
            ) : (
              <Button
                text="Set default"
                size="sm"
                variant="outlined"
                color="secondary"
                onClickHandler={() => setIsDefaultMutation({ unique_id })}
                disabled={isPendingSetDefault}
              />
            ))}
          <Button
            text="Delete"
            size="sm"
            leadingIcon={TrashCan}
            variant="outlined"
            disabled={isPendingDeleteCard}
            color="secondary"
            onClickHandler={() => setIsDeleteCardModalOpen(true)}
          />
          <Modal
            open={isDeleteCardModalOpen}
            closeOnClickOutside
            isMobile={isMobile}
            hasCloseIcon
            supportingText="Payment Details will be removed from the 1-Click Account ."
            title="Are you sure you want to delete the payment method"
            headerIcon={<FeaturedIcon size="xl" color="red" icon={TrashCan} />}
            btnsProps={[
              {
                color: 'primary',
                text: 'Cancel',
                onClickHandler: deleteCardModalCloseHandler,
              },
              {
                color: 'secondary',
                text: 'Delete payment method',
                variant: 'outlined',
                onClickHandler: () => deleteCardHandler(),
              },
            ]}
            onCloseHandler={deleteCardModalCloseHandler}
          />
        </section>
      </section>
      {isSuccessDeleting && (
        <section className="fixed bottom-[1.875rem] left-4 right-4 md:left-[calc(18rem+2.9%)] md:right-[2.9%] z-10">
          <Notification
            show={isSuccessDeleting}
            isMobile={isMobile}
            title="Payment method successfully deleted"
            icon={TrashCan}
            onCloseHandler={() => resetDeleteMutation()}
            state="normal"
            type="alert"
          />
        </section>
      )}
    </section>
  );
};
